import { motion } from "framer-motion";
import { ShieldCheck, Eye, Users } from "lucide-react";

const AIConfidenceScore = () => {
  return (
    <section
      className="py-10 sm:py-20 bg-gradient-to-b from-white to-warm-cream-50"
      aria-label="AI Confidence Score"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-left sm:text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-surface px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-6 border border-line">
            <ShieldCheck className="w-4 h-4 text-plum" />
            <span className="text-xs sm:text-sm font-medium text-plum">
              AI Confidence Score
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6 leading-[1.15]">
            We show what other AI builders{" "}
            <span className="text-gradient-sunset">hide.</span>
          </h2>

          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl sm:mx-auto leading-relaxed">
            Joylo&apos;s AI Confidence Score surfaces uncertainty in real-time.
            When the AI isn&apos;t sure, a certified engineer can step in &mdash;
            before a bug ever reaches production.
          </p>
          <p className="text-base sm:text-lg text-foreground font-semibold mt-3 sm:mt-4">
            Transparency is not a feature. It&apos;s our operating model.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
        >
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-line hover:border-input hover:shadow-soft transition-all duration-300">
            <Eye className="h-7 w-7 sm:h-8 sm:w-8 text-plum mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
              Real-time uncertainty
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Every build is scored on what actually breaks in production. When
              the score is green, you ship. When it isn&apos;t, you see exactly why.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-line hover:border-input hover:shadow-soft transition-all duration-300">
            <Users className="h-7 w-7 sm:h-8 sm:w-8 text-plum mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
              Human-in-the-Loop
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              140 in-house engineers stand behind every line. One click connects
              you to an engineer already in your codebase &mdash; no marketplace,
              no handoffs.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-line hover:border-input hover:shadow-soft transition-all duration-300 sm:col-span-2 md:col-span-1">
            <ShieldCheck className="h-7 w-7 sm:h-8 sm:w-8 text-plum mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
              Production guarantee
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              SLA-backed delivery. Other AI builders ship prototypes. We ship
              products &mdash; production-grade from day one.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIConfidenceScore;
